import classNames from "classnames";
import {
  ChangeEventHandler,
  FocusEventHandler,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { getCaretCoordinates } from "../../utils/caretPosition";
import Dropdown from "../dropdown/dropdown";
import { ErrorText } from "../typography/errorText";

export type TokenOption = {
  token: string;
  label: string;
};

export type TokenTextareaProps = {
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  hasTitle?: boolean;
  title?: string;
  rows?: number;
  fill?: "grey" | "white";
  errors?: string;
  helperText?: ReactNode;
  tokens: TokenOption[];
  containerClassName?: string;
};

const tokenPattern = /(\{\{\w+\}\})/g;
const isToken = (part: string) => /^\{\{\w+\}\}$/.test(part);

const renderHighlighted = (value: string) => {
  const parts = value.split(tokenPattern);

  return (
    <>
      {parts.map((part, idx) =>
        isToken(part) ? (
          <span
            key={idx}
            className="rounded bg-grey-100 font-bold text-grey-950"
          >
            {part}
          </span>
        ) : (
          <span key={idx}>{part}</span>
        ),
      )}
      {"​"}
    </>
  );
};

export const TokenTextarea = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder = "",
  hasTitle = false,
  title = "",
  rows = 4,
  fill = "grey",
  errors = "",
  helperText,
  tokens,
  containerClassName = "",
}: TokenTextareaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const triggerAnchorRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const bracePositionRef = useRef<number | null>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [value]);

  const insertToken = (token: string) => {
    const idx = bracePositionRef.current;
    if (idx === null) {
      return;
    }

    const nextValue = `${value.slice(0, idx)}${token}${value.slice(idx + 1)}`;
    bracePositionRef.current = null;
    onChange(nextValue);

    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) {
        return;
      }
      el.focus();
      const pos = idx + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const newValue = e.target.value;
    const caret = e.target.selectionStart ?? newValue.length;
    const justTypedBrace =
      newValue.length === value.length + 1 &&
      caret > 0 &&
      newValue[caret - 1] === "{";

    onChange(newValue);

    if (justTypedBrace) {
      bracePositionRef.current = caret - 1;

      const anchor = triggerAnchorRef.current;
      if (anchor) {
        const coords = getCaretCoordinates(e.target, caret);
        anchor.style.left = `${coords.left - e.target.scrollLeft}px`;
        anchor.style.top = `${
          coords.top + coords.height - e.target.scrollTop
        }px`;
      }

      triggerRef.current?.click();
    }
  };

  const handleScroll = () => {
    if (overlayRef.current && textareaRef.current) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  return (
    <div className={containerClassName}>
      <div
        className={classNames(
          "group w-full items-center rounded-lg border border-grey-100 bg-grey-50",
          "px-[12px] text-sm font-normal transition-all",
          "focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-grey-950",
          "py-[14px] md:py-[16px]",
          {
            "focus-within:!py-2": !!title,
            "!py-2": !!value && !!title,
            "focus-within:!outline-danger": !!errors,
            "!bg-white": fill === "white",
          },
        )}
      >
        {hasTitle && (
          <div
            className={`hidden text-xs leading-4 text-grey-700 transition-all duration-300 group-focus-within:flex ${value && "!flex"}`}
          >
            {title}
          </div>
        )}

        <div className="relative w-full">
          <div
            ref={overlayRef}
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words text-sm font-normal text-grey-950"
          >
            {renderHighlighted(value)}
          </div>

          <textarea
            ref={textareaRef}
            name={name}
            value={value}
            placeholder={placeholder}
            rows={rows}
            onChange={handleChange}
            onScroll={handleScroll}
            onBlur={onBlur}
            style={{ caretColor: "#04000A" }}
            className="relative block w-full resize-none bg-transparent font-normal text-transparent outline-none placeholder:font-normal placeholder:text-grey-700"
          />

          <div
            ref={triggerAnchorRef}
            className="pointer-events-none absolute left-0 top-0 h-px w-px overflow-visible opacity-0"
          >
            <Dropdown
              menuItems={tokens.map((option) => ({
                name: (
                  <span className="font-bold text-grey-950">
                    {option.label}
                  </span>
                ),
                value: option.token,
                onClick: () => insertToken(option.token),
              }))}
              menuClassName="!z-[999]"
              menuItemsClassName="!items-start"
              position="bottom start"
              onClose={() => {
                bracePositionRef.current = null;
              }}
            >
              <button
                ref={triggerRef}
                type="button"
                tabIndex={-1}
                aria-hidden
                className="h-px w-px"
              />
            </Dropdown>
          </div>
        </div>
      </div>

      {errors && <ErrorText text={errors} />}

      {helperText && !errors && (
        <p className="mt-2 text-xs text-grey-500">{helperText}</p>
      )}
    </div>
  );
};
